<template>
  <aside class="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:w-64 lg:bg-white lg:border-r lg:border-gray-200 z-50">
    <!-- Logo/Brand -->
    <div class="flex items-center h-16 px-6 border-b border-gray-200">
      <img src="@/assets/Shim-logo.png" alt="Shim" class="h-8 w-auto" />
    </div>

    <!-- Navigation -->
    <nav class="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
      <!-- Messages -->
      <router-link
        to="/app"
        class="flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors"
        :class="isActive('/app') ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:bg-gray-50'"
      >
        <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
        </svg>
        Messages
      </router-link>

      <!-- Customers -->
      <router-link
        to="/customers"
        class="flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors"
        :class="isActive('/customers') ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:bg-gray-50'"
      >
        <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
        </svg>
        Customers
      </router-link>

      <!-- Quotes -->
      <router-link
        to="/quotes"
        class="flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors"
        :class="isActive('/quotes') ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:bg-gray-50'"
      >
        <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
        </svg>
        Quotes
      </router-link>

      <!-- History -->
      <router-link
        to="/history"
        class="flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors"
        :class="isActive('/history') ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:bg-gray-50'"
      >
        <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        History
      </router-link>

      <!-- Settings -->
      <router-link
        to="/settings"
        class="flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors"
        :class="isActive('/settings') ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:bg-gray-50'"
      >
        <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
        </svg>
        Account
      </router-link>

      <!-- Team Management (if admin) -->
      <router-link
        v-if="companyStore.isCompanyAdmin"
        to="/team"
        class="flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors"
        :class="isActive('/team') ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:bg-gray-50'"
      >
        <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
        </svg>
        Team
      </router-link>
    </nav>

    <!-- Team Upgrade Card (Solo Contractors Only) -->
    <div v-if="!companyStore.isCompanyAdmin && !companyStore.isTeamMember" class="p-4 border-t border-gray-200">
      <div class="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg p-4 text-white">
        <div class="flex items-center mb-2">
          <span class="text-2xl mr-2">🚀</span>
          <h3 class="font-bold text-sm">Grow Your Team</h3>
        </div>
        <p class="text-xs text-blue-100 mb-3">
          Invite staff members and collaborate together
        </p>
        <button
          @click="router.push('/settings')"
          class="w-full px-3 py-2 bg-white text-purple-600 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
        >
          Upgrade to Teams
        </button>
      </div>
    </div>

    <!-- Quick Record Button (Teams Only) -->
    <div v-else class="p-4 border-t border-gray-200">
      <button
        @click="router.push('/record')"
        class="w-full flex items-center justify-center px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium shadow-sm"
      >
        <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
          <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
        </svg>
        Record Message
      </button>
    </div>

    <!-- User Profile & Logout -->
    <div class="p-4 border-t border-gray-200">
      <div class="flex items-center mb-3">
        <div class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
          <svg v-if="!userData?.photoURL" class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
          </svg>
          <img v-else :src="userData.photoURL" alt="Profile" class="w-full h-full object-cover" />
        </div>
        <div class="ml-3 flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-900 truncate">
            {{ userData?.displayName || userData?.name || 'User' }}
          </p>
          <p class="text-xs text-gray-500 truncate">
            {{ userData?.email }}
          </p>
        </div>
      </div>

      <!-- Logout Button -->
      <button
        @click="handleLogout"
        class="w-full flex items-center justify-center px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
      >
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
        </svg>
        Sign Out
      </button>
    </div>
  </aside>
</template>

<script setup>
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useCompanyStore } from '@/stores/company'
import { computed } from 'vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const companyStore = useCompanyStore()

const userData = computed(() => authStore.userData)

const isActive = (path) => {
  return route.path === path
}

const handleLogout = async () => {
  try {
    await authStore.logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout error:', error)
  }
}
</script>
